import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { eye, eyeOff } from 'ionicons/icons';
import { IonicButton, IonicInput } from 'ionic-ui';
import Layout from './Layout';
// import { toast } from 'react-toastify';
// import { useDashboardController, setBusy } from '../../context';
// import { z } from 'zod';
import {
  // confirmPasswordSchema,
  // passwordSchema,
  emailSchema,
  passwordResetSchema,
  phoneSchema,
  // tokenSchema,
} from '../../schemas/validation';
import { useCountDown } from '../../hooks/useTimer';
import {
  resetPassword,
  sendPasswordResetToken,
} from '../../services/changeReset/resetPassword';
import useIonicToast from '../../hooks/useIonicToast';
import { usePageLoader } from '../../context/pageLoader';

const ResetPassword: React.FC = () => {
  const { setBusy } = usePageLoader();
  const location = useLocation();
  const history = useHistory();
  const { presentIonToast } = useIonicToast();
  const { counterTime, isCounting, startCountDown, stopCountDown } =
    useCountDown(1);

  const inputRefs = {
    token: React.useRef(null),
    password: React.useRef(null),
    confirm_password: React.useRef(null),
  } as const;

  // Set initial state for the form data
  // const [countdown, setCountdown] = React.useState<null | string>('');

  const [formData, setFormData] = React.useState({
    identifier:
      location.state &&
      'identifier' in (location.state as Record<string, string>)
        ? ((location.state as { identifier: string }).identifier ?? '')
        : location.state &&
            'email' in (location.state as Record<string, string>)
          ? ((location.state as { email: string }).email ?? '')
          : '',
    mode:
      location.state && 'mode' in (location.state as Record<string, string>)
        ? ((location.state as { mode: 'email' | 'phone' }).mode ?? 'email')
        : 'email',
    via:
      location.state && 'via' in (location.state as Record<string, string>)
        ? ((
            location.state as {
              via: 'email' | 'sms' | 'whatsapp';
            }
          ).via ?? 'email')
        : 'email',
    token: '',
    password: '',
    confirm_password: '',
  });
  const [formError, setFormError] = React.useState({
    token: '',
    password: '',
    confirm_password: '',
  });

  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] =
    React.useState(false);

  // Handle form data changes
  const handleInputChange = (event: InputEvent) => {
    const { name, value } = event.target as HTMLInputElement;
    setFormData({ ...formData, [name]: value });
    setFormError({ ...formError, [name]: '' });
  };

  // Handle form submission
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (
    event
  ) => {
    event.preventDefault();

    // const validationSchema = z
    //   .object({
    //     token: tokenSchema,
    //     password: passwordSchema,
    //     confirm_password: confirmPasswordSchema,
    //   })
    //   .refine((data) => data.password === data.confirm_password, {
    //     message: "Passwords don't match",
    //     path: ['confirm_password'],
    //   });

    const validate = passwordResetSchema.safeParse(formData);

    if (!validate.success) {
      const errs: { [key: string]: string | number | boolean } = {};
      validate.error.issues.forEach((issue) => {
        const field = issue.path[
          issue.path.length - 1
        ] as keyof typeof inputRefs;
        if (!(field in errs)) errs[field] = issue.message;
        if (inputRefs[field].current)
          (inputRefs[field].current as HTMLElement).scrollIntoView();
      });
      setFormError({ ...formError, ...errs });
      return;
    }

    const identifierIsValid =
      formData.mode === 'phone'
        ? phoneSchema.safeParse(formData.identifier).success
        : emailSchema.safeParse(formData.identifier).success;

    if (!identifierIsValid) {
      presentIonToast(
        formData.mode === 'phone'
          ? 'Enter a valid phone number'
          : 'Enter a valid email address',
        'error'
      );
      return;
    }

    setBusy(true);
    startCountDown();
    try {
      const payload = {
        token: formData.token,
        password: formData.password,
        confirm_password: formData.confirm_password,
        ...(formData.mode === 'phone'
          ? {
              phone: formData.identifier.trim(),
              via: formData.via,
            }
          : {
              email: formData.identifier.trim().toLowerCase(),
              via: 'email' as const,
            }),
      };

      await resetPassword<typeof payload>(payload);
      stopCountDown();
      presentIonToast('Password reset successfully!');
      history.replace('/auth/sign-in');
    } catch (err) {
      let msg = 'something went wrong, try again.';
      if (err instanceof Error) {
        console.error('Error:', err.message);
      } else if (typeof err === 'object' && err && 'code' in err) {
        if (err.code === 401) msg = 'token is invalid';
      }
      presentIonToast(msg, 'error');
    } finally {
      setBusy(false);
    }
  };

  const resend = (async (e: MouseEvent) => {
    e.preventDefault();
    if (isCounting) return;

    try {
      setBusy(true);
      await sendPasswordResetToken(formData);
      startCountDown();
    } catch (err) {
      let msg = 'something went wrong, try again.';
      if (err instanceof Error) {
        console.error('Error:', err.message);
      } else if (typeof err === 'object' && err && 'code' in err) {
        if (err.code === 401) msg = 'token is invalid';
      }
      presentIonToast(msg, 'error');
    } finally {
      setBusy(false);
    }
  }) as unknown as React.MouseEventHandler<HTMLAnchorElement>;

  return (
    <Layout
      uiType="signin"
      title="Reset Password"
      description={`Use the OTP sent to your ${
        formData.mode === 'phone' ? 'phone number' : 'email'
      } to proceed.`}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-sm">
        {/* Token */}
        <IonicInput
          ref={inputRefs.token}
          containerClass="max-w-44"
          size="xlg"
          className="text-center tracking-[10px] max-w-full"
          maxLength={6}
          type="text"
          name="token"
          label="OTP"
          aria-label="OTP"
          placeholder="XXXXXX"
          onChange={handleInputChange}
          value={formData.token}
          hint={formError.token}
          hintClass="text-red-600 dark:text-red-600"
        />

        {/* Password Input */}
        <IonicInput
          ref={inputRefs.password}
          innerContainerClass="pr-0"
          size="xlg"
          name="password"
          type={passwordVisible ? 'text' : 'password'}
          label="Password"
          aria-label="Password"
          placeholder="********"
          suffix={
            <IonicButton
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                setPasswordVisible(!passwordVisible);
              }}
              variant="ghost"
              size="lg"
              className="aspect-square"
            >
              {!passwordVisible && (
                <IonIcon
                  icon={eye}
                  className="text-text-dark-high dark:text-text-light-high text-xlg"
                />
              )}
              {passwordVisible && (
                <IonIcon
                  icon={eyeOff}
                  className="text-text-dark-high dark:text-text-light-high text-xlg"
                />
              )}
            </IonicButton>
          }
          onChange={handleInputChange}
          value={formData.password}
          hint={formError.password}
          hintClass="text-red-600 dark:text-red-600"
        />

        {/* Confirm Password Input */}
        <IonicInput
          ref={inputRefs.confirm_password}
          size="xlg"
          type={confirmPasswordVisible ? 'text' : 'password'}
          name="confirm_password"
          label="Confirm Password"
          aria-label="Confirm Password"
          placeholder="********"
          suffix={
            <IonicButton
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                setConfirmPasswordVisible(!confirmPasswordVisible);
              }}
              variant="ghost"
              size="lg"
              className="aspect-square -mr-2"
            >
              {!confirmPasswordVisible && (
                <IonIcon
                  icon={eye}
                  className="text-text-dark-high dark:text-text-light-high"
                />
              )}
              {confirmPasswordVisible && (
                <IonIcon
                  icon={eyeOff}
                  className="text-text-dark-high dark:text-text-light-high"
                />
              )}
            </IonicButton>
          }
          onChange={handleInputChange}
          value={formData.confirm_password}
          hint={formError.confirm_password}
          hintClass="text-red-600 dark:text-red-600"
        />

        {/* Login Button */}
        <IonicButton type="submit" size="xlg" className="w-full">
          Continue
        </IonicButton>

        <IonicButton
          type="button"
          size="xlg"
          className="w-full"
          variant="outline"
          disabled={isCounting}
          onClick={resend}
        >
          {isCounting && `Resend OTP in ${counterTime}`}
          {!isCounting && <span>Resend OTP</span>}
        </IonicButton>

        {formData.mode === 'phone' && (
          <IonicInput
            size="xlg"
            type="select"
            label="Receive via"
            aria-label="Receive via"
            value={formData.via}
            options={[
              { label: 'SMS', value: 'sms' },
              { label: 'WhatsApp', value: 'whatsapp' },
            ]}
            onChange={(value: string) =>
              setFormData({ ...formData, via: value as 'sms' | 'whatsapp' })
            }
          />
        )}
      </form>
    </Layout>
  );
};

export default ResetPassword;
